/**
 * @lodex-pro/web-dispatch/auth/TenantSelect — first-login tenant selection.
 *
 * Quick 260614-pli — rendered by App.tsx when an authenticated user has no
 * active tenant AND belongs to more than one tenant. Single-membership users
 * are auto-selected by `useActiveTenant` and never see this. On click, the
 * parent's `onSelect` calls `useActiveTenant().selectTenant`, which POSTs
 * /v1/auth/switch-tenant (no re-auth — this is the initial selection, not a
 * mid-session switch).
 *
 * Reuses the centered dark `.auth-shell`/`.auth-card` styling from styles.css
 * (same surface as SignInPage) for visual consistency.
 */

import type { FC } from 'react';
import type { TenantMembership } from '@lodex-pro/web-shared';

export interface TenantSelectProps {
  readonly memberships: ReadonlyArray<TenantMembership>;
  readonly onSelect: (tenantId: string) => void;
  /** Disable the buttons while a selection is in flight. */
  readonly busy?: boolean;
}

export const TenantSelect: FC<TenantSelectProps> = ({ memberships, onSelect, busy = false }) => {
  return (
    <main className="auth-shell" aria-label="Choose a tenant">
      <div className="auth-card">
        <h1 className="auth-brand">lodeX Pro Dispatch</h1>
        <p className="auth-subtitle">Choose a workspace to continue</p>
        <ul aria-label="Your tenants" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {memberships.map((m) => (
            <li key={m.tenantId} style={{ marginBottom: '8px' }}>
              <button
                type="button"
                disabled={busy}
                onClick={(): void => {
                  onSelect(m.tenantId);
                }}
                style={{
                  width: '100%',
                  minHeight: '52px',
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-secondary)',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  fontSize: '16px',
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                <span style={{ fontWeight: 600 }}>{m.tenantName}</span>
                <span style={{ color: 'var(--color-text-tertiary)', fontSize: '14px' }}>
                  {' '}
                  — {m.role}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
};
